import { useRef, useState } from "react";
import { MapPin, Loader2 } from "lucide-react";
import api from "@/lib/api";

export function AddressAutocomplete({ value, onChange, onSelect, className, testid = "address-autocomplete", placeholder, required }) {
  const [sug, setSug] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(-1);
  const timer = useRef(null);
  const blurTimer = useRef(null);

  const query = (v) => {
    onChange(v);
    setActive(-1);
    clearTimeout(timer.current);
    if (v.trim().length < 3) { setSug([]); setOpen(false); return; }
    timer.current = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/geo/autocomplete", { params: { q: v } });
        setSug(data.suggestions || []);
        setOpen((data.suggestions || []).length > 0);
      } catch { setSug([]); setOpen(false); }
      finally { setLoading(false); }
    }, 300);
  };

  const choose = (s) => {
    onChange(s.label);
    onSelect && onSelect(s);
    setOpen(false);
    setSug([]);
  };

  const onKeyDown = (e) => {
    if (!open || !sug.length) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setActive((a) => Math.min(a + 1, sug.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
    else if (e.key === "Enter" && active >= 0) { e.preventDefault(); choose(sug[active]); }
    else if (e.key === "Escape") { setOpen(false); }
  };

  return (
    <div className="relative">
      <input
        required={required}
        data-testid={testid}
        className={className}
        value={value}
        placeholder={placeholder}
        autoComplete="off"
        onChange={(e) => query(e.target.value)}
        onKeyDown={onKeyDown}
        onFocus={() => sug.length && setOpen(true)}
        onBlur={() => { blurTimer.current = setTimeout(() => setOpen(false), 150); }}
      />
      {loading && <Loader2 className="w-4 h-4 text-[#8a8a86] animate-spin absolute right-3 top-1/2 -translate-y-1/2" />}
      {open && sug.length > 0 && (
        <ul data-testid={`${testid}-list`} className="absolute z-50 left-0 right-0 mt-1 bg-white border border-[#E5E5E2] rounded-md shadow-lg max-h-64 overflow-auto">
          {sug.map((s, i) => (
            <li key={s.label}>
              <button
                type="button"
                data-testid={`${testid}-option-${i}`}
                onMouseDown={(e) => { e.preventDefault(); clearTimeout(blurTimer.current); choose(s); }}
                onMouseEnter={() => setActive(i)}
                className={`w-full text-left px-3 py-2 text-sm flex items-start gap-2 ${i === active ? "bg-[#F4F7FD]" : "hover:bg-[#F7F7F5]"}`}
              >
                <MapPin className="w-4 h-4 text-[#1F3C88] shrink-0 mt-0.5" />
                <span>{s.label}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
